import React, { FunctionComponent, useEffect } from 'react';
import { RateBar } from '../Styles/Materials';
import { outlineStarIcon, starIcon } from '../../../Assets/images/Icons';
import { useTranslation } from 'react-i18next';
import { issueIconOutline } from '../Assets/IssueIcon';
import { Tooltip as Tippy } from 'react-tippy';
import IssueTool from './IssueTool';
import RateTool from './RateTool';
import { IReview } from '../../../__core/Interfaces/IReview';

type OptionsBarProps = {
  addReview: (justification: string | null, grade: number) => Promise<void>;
  addIssue: (message: string, selected: string) => Promise<void>;
  review: IReview | null;
  showIssueSection?: boolean;
  showStarSection?: boolean;
  materialContainerRef: React.RefObject<HTMLDivElement>;
};

const OptionsBar: FunctionComponent<OptionsBarProps> = ({
  addIssue,
  addReview,
  review,
  showIssueSection = true,
  showStarSection = true,
  materialContainerRef,
}) => {
  const { t } = useTranslation();

  const [rating, setRating] = React.useState(0);
  const [hoverRating, setHoverRating] = React.useState(0);
  const [showRateBox, setShowRateBox] = React.useState(false);
  const [showIssueBox, setShowIssueBox] = React.useState(false);

  const openIssueBox = (): void => setShowIssueBox(true);
  const closeIssueBox = (): void => setShowIssueBox(false);

  const openRateBox = (): void => setShowRateBox(true);
  const closeRateBox = (): void => {
    setShowRateBox(false);
    setRating(review ? review.grade : 0);
  };

  const onMouseEnter = (index: number): void => setHoverRating(index);
  const onMouseLeave = (): void => setHoverRating(0);
  const onSaveRating = (index: number): void => {
    setRating(index);
    openRateBox();
  };
  const starsIterator = [1, 2, 3, 4, 5];

  const getStarIcon = (index: number): React.ReactNode => {
    if (hoverRating >= index) {
      return starIcon;
    } else if (!hoverRating && rating >= index) {
      return starIcon;
    }
    return outlineStarIcon;
  };

  const onAddReview = (justification: string | null): void => {
    addReview(justification, rating);
  };

  useEffect(() => {
    setRating(review ? review.grade : 0);
  }, [review]);

  return (
    <RateBar>
      <div>
        {showStarSection && (
          <>
            <Tippy
              arrowSize="big"
              theme="light"
              arrow
              html={
                <RateTool
                  addReview={onAddReview}
                  grade={rating}
                  onClose={closeRateBox}
                  review={review}
                />
              }
              open={showRateBox}
              interactive
            />
            <div className="rate-label">{t('Rate')}:</div>
            {starsIterator.map((index) => (
              <div
                key={`rate-${index}`}
                onMouseEnter={() => onMouseEnter(index)}
                onMouseLeave={() => onMouseLeave()}
                onClick={() => onSaveRating(index)}
                className="star"
              >
                {getStarIcon(index)}
              </div>
            ))}
          </>
        )}
      </div>
      {showIssueSection && (
        <Tippy
          arrowSize="big"
          theme="light"
          arrow
          html={
            <IssueTool
              materialContainerRef={materialContainerRef}
              addIssue={addIssue}
              onClose={closeIssueBox}
              showIssueBox={showIssueBox}
            />
          }
          open={showIssueBox}
          interactive
        >
          <div className="issue-section" onClick={openIssueBox}>
            {issueIconOutline}
            <div>{t('Request issue')}</div>
          </div>
        </Tippy>
      )}
    </RateBar>
  );
};

export default OptionsBar;
